/** India Standard Time helpers for API datetimes (DB / JSON). */

const IST_TIMEZONE = 'Asia/Kolkata';

export function uppercaseMeridiem(text) {
  return String(text).replace(/\b(am|pm)\b/gi, (m) => m.toUpperCase());
}

/**
 * Parse API/DB datetime to epoch ms.
 * - Z or +05:30 etc. → true instant
 * - Naive ISO (no zone) → IST wall-clock (matches SQL IST storage)
 */
export function parseApiDateTimeMs(value) {
  if (value == null || value === '') return NaN;
  if (typeof value === 'number') return value;
  if (value instanceof Date) return value.getTime();

  const s = String(value).trim();
  if (!s) return NaN;

  if (/[Zz]$/.test(s) || /[+-]\d{2}:?\d{2}(?::\d{2})?$/.test(s)) {
    const instant = new Date(s).getTime();
    return Number.isNaN(instant) ? NaN : instant;
  }

  const m = s.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d+))?)?)?/
  );
  if (m) {
    const [, y, mo, d, h = '0', mi = '0', sec = '0', frac = '0'] = m;
    const ms = frac ? Math.round(Number(`0.${frac}`) * 1000) : 0;
    const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;
    return Date.UTC(+y, +mo - 1, +d, +h, +mi, +parseInt(sec, 10), ms) - IST_OFFSET_MS;
  }

  const fallback = new Date(s).getTime();
  return Number.isNaN(fallback) ? NaN : fallback;
}

/** @deprecated alias */
export const parseIstMs = parseApiDateTimeMs;

export function getIstNowMs(referenceNow) {
  if (referenceNow instanceof Date) return referenceNow.getTime();
  if (typeof referenceNow === 'number') return referenceNow;
  return Date.now();
}

export function toIstDateString(ms = Date.now()) {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: IST_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date(ms));
  const y = parts.find((p) => p.type === 'year')?.value;
  const m = parts.find((p) => p.type === 'month')?.value;
  const d = parts.find((p) => p.type === 'day')?.value;
  return y && m && d ? `${y}-${m}-${d}` : '';
}

export function isSameIstDay(a, bMs = Date.now()) {
  const aMs = typeof a === 'number' ? a : parseApiDateTimeMs(a);
  const b = typeof bMs === 'number' ? bMs : parseApiDateTimeMs(bMs);
  if (Number.isNaN(aMs) || Number.isNaN(b)) return false;
  return toIstDateString(aMs) === toIstDateString(b);
}

export function endOfIstDayMs(ms) {
  const dateStr = toIstDateString(ms);
  if (!dateStr) return ms;
  const [y, m, d] = dateStr.split('-').map(Number);
  const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;
  return Date.UTC(y, m - 1, d, 23, 59, 59, 999) - IST_OFFSET_MS;
}

export function startOfIstDayMs(msOrDateStr) {
  const dateStr = typeof msOrDateStr === 'string' ? msOrDateStr : toIstDateString(msOrDateStr);
  if (!dateStr) return Number.NaN;
  const [y, m, d] = dateStr.split('-').map(Number);
  const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;
  return Date.UTC(y, m - 1, d, 0, 0, 0, 0) - IST_OFFSET_MS;
}

export function startOfNextIstDayMs(ms) {
  const dateStr = toIstDateString(ms);
  if (!dateStr) return Number.NaN;
  const [y, m, d] = dateStr.split('-').map(Number);
  const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;
  return Date.UTC(y, m - 1, d + 1, 0, 0, 0, 0) - IST_OFFSET_MS;
}

/** Max milliseconds in one IST calendar day. */
export const MAX_IST_DAY_MS = 24 * 60 * 60 * 1000;

export function capMsToDailyLimit(ms) {
  return Math.min(Math.max(0, Number(ms) || 0), MAX_IST_DAY_MS);
}

export function formatIstDateTime(value) {
  const ms = parseApiDateTimeMs(value);
  if (Number.isNaN(ms)) return '—';

  const formatted = new Intl.DateTimeFormat('en-GB', {
    timeZone: IST_TIMEZONE,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  }).format(new Date(ms));

  return uppercaseMeridiem(formatted);
}

export function formatIstTime(value) {
  const ms = parseApiDateTimeMs(value);
  if (Number.isNaN(ms)) return '—';

  const formatted = new Intl.DateTimeFormat('en-GB', {
    timeZone: IST_TIMEZONE,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  }).format(new Date(ms));

  return uppercaseMeridiem(formatted);
}
